import {Fragment, ReactElement} from "react"
import classNames from "classnames"
import {Form, InputNumber} from "antd"
import {t} from "@ui/common/intl"
import {TBoatDimensions, TBoatDimensionUnits, TDimensionUnits} from "@ui/api/user"
import {BoatInfoHeader, TBoatInfoPageProps} from "./common"

type TSwitchProps<T extends [TDimensionUnits, TDimensionUnits]> = {
  values: T
  value?: T[number] | null
  onChange?: (value: T[number]) => void
}
const Switch = <T extends [TDimensionUnits, TDimensionUnits]>({
  values,
  value,
  onChange
}: TSwitchProps<T>): ReactElement => {
  return (
    <div className="switch">
      <div
        onClick={() => onChange?.(values[0])}
        className={classNames("left", {"selected": !value || value === values[0]})}
      >
        {t(`profile.boat.dimensions.unit.${values[0]}`)}
      </div>
      <div
        onClick={() => onChange?.(values[1])}
        className={classNames("right", {"selected": value === values[1]})}
      >
        {t(`profile.boat.dimensions.unit.${values[1]}`)}
      </div>
    </div>
  )
}

type TDimensionProps<T extends [TDimensionUnits, TDimensionUnits]> = {
  prop: keyof TBoatDimensions
  unitValue?: T[number]
  unitValues: T,
  onShowHint: (key: keyof TBoatDimensions) => void
}
const Dimension = <T extends [TDimensionUnits, TDimensionUnits]>({
  prop,
  unitValues,
  onShowHint
}: TDimensionProps<T>): ReactElement => {
  return (
    <Form.Item<TBoatDimensions> label={t(`profile.boat.${prop}.title`)}>
      <div>
        <div className="dimension-row">
          <Form.Item<TBoatDimensions> name={prop}>
            <InputNumber
              min={1}
              placeholder={t(`profile.boat.${prop}.placeholder`)}
            />
          </Form.Item>
          <Form.Item<TBoatDimensionUnits> name={`${prop}_unit`}>
            <Switch<T> values={unitValues} />
          </Form.Item>
        </div>
        <div className="hint" onClick={() => onShowHint(prop)}>
          {t(`profile.boat.${prop}.hint.title`)}
        </div>
      </div>
    </Form.Item>
  )
}

const Dimensions = ({onShowHint}: TBoatInfoPageProps): ReactElement => {
  return (
    <Fragment>
      <BoatInfoHeader
        title={t("profile.boat.info.dimensions.header")}
        text={t("profile.boat.info.dimensions.text")}
      />
      <Dimension
        prop="length"
        unitValues={["m", "ft"]}
        onShowHint={onShowHint}
      />
      <Dimension
        prop="width"
        unitValues={["m", "ft"]}
        onShowHint={onShowHint}
      />
      <Dimension
        prop="draft"
        unitValues={["m", "ft"]}
        onShowHint={onShowHint}
      />
      <Dimension
        prop="height"
        unitValues={["m", "ft"]}
        onShowHint={onShowHint}
      />
      <Dimension
        prop="cruising_speed"
        unitValues={["kmph", "kn"]}
        onShowHint={onShowHint}
      />
      <Dimension
        prop="fuel_rate"
        unitValues={["lph", "lpkm"]}
        onShowHint={onShowHint}
      />
    </Fragment>
  )
}

export default Dimensions
